import { Fragment } from 'react';

import { Skeleton } from '@playbooks/molecules/skeletons';
import { Button } from '@playbooks/ui/components/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@playbooks/ui/components/card';
import { CardMedia } from '@playbooks/ui/components/card';
import type { IconValue } from '@playbooks/ui/components/icon';
import { FasIcon, Icon, resolveIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

import { cva, VariantProps } from 'class-variance-authority';

const selectCardHeaderVariants = cva('aspect-[1/1] rounded-control flex-middle shrink-0 overflow-hidden', {
	variants: { size: { default: 'w-10', lg: 'w-12' } },
	defaultVariants: { size: 'default' },
});

type SelectCardProps = VariantProps<typeof selectCardHeaderVariants> & {
	layout?: 'default' | 'draggable';
	icon?: IconValue;
	badges?: React.ReactNode;
	media?: React.ReactNode;
	photo?: string;
	pretitle?: React.ReactNode;
	title?: React.ReactNode;
	subtitle?: React.ReactNode;
	text?: React.ReactNode;
	selected?: boolean;
	loading?: boolean;
	taskRunning?: boolean;
	onClick?: () => void;
	className?: string;
	actions?: React.ReactNode;
};

const SelectCard = ({
	layout = 'default',
	size = 'default',
	icon = 'database',
	badges = null,
	media = null,
	photo = '',
	pretitle = '',
	title = '',
	subtitle = '',
	text = '',
	selected = false,
	loading = false,
	taskRunning = false,
	onClick,
	className = '',
	actions = null,
}: SelectCardProps) => {
	// Theme
	const classNames = {
		root: cn(
			'border-b flex-between gap-3 group-hover:border-border-strong group-hover:bg-surface-card-hover',
			className,
		),
		header: selectCardHeaderVariants({ size }),
	};

	// Render
	if (loading) return <Skeleton layout='select' className={className} />;

	return (
		<Card size='sm' className={classNames.root}>
			<span className='flex-start-top gap-3 w-full'>
				{layout === 'draggable' && (
					<span className='cursor-grab'>
						<FasIcon icon='grip-dots-vertical' className='text-xs' />
					</span>
				)}
				{media ? (
					<Fragment>{media}</Fragment>
				) : (
					<CardHeader className={classNames.header}>
						{photo ? (
							<img src={photo} alt='' className='w-full' />
						) : (
							<CardMedia>
								<Icon {...resolveIcon(icon, 'fad')} aria-hidden />
							</CardMedia>
						)}
					</CardHeader>
				)}
				<CardContent className='w-full'>
					{pretitle && <CardDescription>{pretitle}</CardDescription>}
					<span className='flex-start gap-4'>
						<CardTitle>{title}</CardTitle>
						{badges && <span className='flex-start gap-4'>{badges}</span>}
					</span>
					{subtitle && <CardDescription className='line-clamp-2'>{subtitle}</CardDescription>}
					{text && <CardDescription>{text}</CardDescription>}
				</CardContent>
			</span>
			<CardAction className='self-center'>
				{actions || (
					<Button
						variant='outline'
						size='icon'
						aria-label={selected ? 'Selected' : 'Select'}
						data-active={selected}
						isLoading={taskRunning}
						onClick={onClick}>
						<Icon data-icon='inline-start' type='far' icon={selected ? 'check' : 'plus'} aria-hidden />
					</Button>
				)}
			</CardAction>
		</Card>
	);
};

export { SelectCard };
export type { SelectCardProps };
